/**
 * Transforms the flat response from POST /v1/us/overview
 * into the nested structure the frontend expects from the old API.
 *
 * The old code expects Celsius/mm and converts to F/inches for display,
 * so we back-convert values that the new API already provides in F/inches.
 */
export function adaptOverviewResponse(data: Record<string, any>) {
  // Drought
  const Drought = {
    score: data.drought_rating,
    baseline_avg: data.drought_wassi_2015,
    future_avg_2050: data.drought_wassi_2050_rcp85,
  }

  // Heat - back-convert threshold from F to C (old code does C -> F at display)
  const heatThresholdC =
    data.heat_threshold_f != null ? (data.heat_threshold_f - 32) * (5 / 9) : null
  const HeatMay22 = {
    score: data.heat_rating,
    threshold: heatThresholdC,
    future_avg_2050_counts: data.heat_events_2050_rcp85,
    baseline_avg_counts: null, // not available in new API
  }

  // Fire
  const FireMay22 = {
    score: data.fire_rating,
    baseline_avg_magnitude: data.fire_threshold_fwi,
    baseline_avg_counts: data.fire_events_1990,
    future_avg_2050_counts: data.fire_events_2050_rcp85,
    FLEP8: data.fire_FLEP8,
    FLEP4: data.fire_FLEP4,
    BP: data.fire_chance_30yr,
  }

  // Storm/Precip - back-convert inches to mm (old code does mm -> inches at display)
  const toMm = (inches: number | null | undefined) =>
    inches != null ? inches * 25.4 : null
  const Storm = {
    score: data.precip_rating,
    threshold: toMm(data.precip_threshold_in),
    baseline_avg_annual: toMm(data.precip_annual_1990_in),
    future_avg_2050_annual: toMm(data.precip_annual_2050_rcp85_in),
    baseline_avg_totals: toMm(data.precip_totals_1990_in),
    future_avg_2050_totals: toMm(data.precip_totals_2050_rcp85_in),
    baseline_avg_counts: null, // not available in new API
    future_avg_2050_counts: null, // not available in new API
  }

  // Flood - new API provides a direct composite rating
  const Flood = {
    score: data.flood_rating,
    _directRating: true,
    score_2050:
      data.flood_tidal_events_2050 != null
        ? (data.flood_tidal_events_2050 * 300) / 365
        : 0,
    tidal_events_2050: data.flood_tidal_events_2050,
    chance_30yr: data.flood_chance_30yr,
    expected_depth_in: data.flood_expected_depth_30yr_in,
  }

  const InlandFlood = {
    risk: data.flood_fema_risk,
  }

  // Caddies (pluvial flood) - back-convert depth inches to cm
  const Caddies = {
    thirty_year_prob: data.flood_chance_30yr,
    thirty_year_depth:
      data.flood_expected_depth_30yr_in != null
        ? data.flood_expected_depth_30yr_in * 2.54
        : null,
  }

  // Wind
  const Wind = {
    score: data.wind_rating,
    frequency_1990: data.wind_frequency_1990_rcp85,
    frequency_2050: data.wind_frequency_2050_rcp85,
    intensity_1990_mph: data.wind_intensity_1990_rcp85_mph,
    intensity_2050_mph: data.wind_intensity_2050_rcp85_mph,
    tornado_count: data.wind_tornado_count,
    tornado_magnitude_max: data.wind_tornado_magnitude_max,
  }

  // No separate fluvial or storm surge data from new API
  const Lisflood_rf = {}
  const Storm_surge = {}

  // Averages not available in new API
  const Averages = null

  return {
    Drought,
    HeatMay22,
    FireMay22,
    // CityPage/StatePage look up "Heat"/"Fire" directly (no redirect)
    Heat: HeatMay22,
    Fire: FireMay22,
    Storm,
    Flood,
    InlandFlood,
    Caddies,
    Lisflood_rf,
    Storm_surge,
    Wind,
    Averages,
  }
}
